import { useEffect, useMemo, useRef, useState } from "react";
import FilterBodyOffcanvas from "../filtersTrafico/filterBodyOffcanvas";

const PAGE_SIZES = [4, 9, 16];

const ensureMutedUrl = (url) => {
  if (!url) return "";
  const lower = String(url).toLowerCase();
  let next = String(url);
  if (!lower.includes("mute=") && !lower.includes("muted=")) {
    next += `${next.includes("?") ? "&" : "?"}mute=true`;
  }
  if (!lower.includes("autoplay=")) {
    next += `${next.includes("?") ? "&" : "?"}autoplay=true`;
  }
  return next;
};

const resolveLiveStreamUrl = (stream) => {
  const deviceId = stream?.deviceId || "";
  const raw = stream?.streamId || stream?.streamUrl || stream?.url || "";
  if (raw && String(raw).toLowerCase().includes("play.html")) return ensureMutedUrl(raw);

  const txt = String(raw || "");
  const match = txt.match(/LiveApp\/([^/?#]+)/i);
  const name =
    match?.[1] ||
    (txt.includes("/") ? txt.split("/").filter(Boolean).pop() : txt) ||
    `${deviceId}_JT800`;
  return ensureMutedUrl(`https://streaming.cargotronics.com:5443//LiveApp/play.html?name=${name}`);
};

const getCompanyFilterCandidates = (item) =>
  [
    item?.NombreEmpresa,
    item?.EmpresaConsulta1,
    item?.EmpresaConsulta2,
    item?.EmpresaConsulta3,
  ]
    .map((value) => String(value ?? "").trim())
    .filter((value) => value.length > 0 && value.toLowerCase() !== "null");

const applyTrafficFilters = (items, payload) => {
  const filters = payload?.filters || [];
  if (!filters.length) return items;
  return items.filter((item) =>
    filters.every((entry) => {
      const key = Object.keys(entry)[0];
      const value = entry[key];
      if (key === "filterEmpresa") {
        return getCompanyFilterCandidates(item).some((company) => value.includes(company));
      }
      if (key === "filterRuta") return value.includes(item.DescripcionRuta);
      if (key === "filterEstado") return value.includes(item.TipoReporte);
      if (key === "filterDispositivo") return value.includes(item.FKLokDeviceID);
      return true;
    })
  );
};

const swapByIndex = (list, a, b) => {
  const next = [...list];
  if (a < 0 || b < 0 || a >= next.length || b >= next.length) return next;
  const temp = next[a];
  next[a] = next[b];
  next[b] = temp;
  return next;
};

const LivesGridManager = ({
  listContratos = [],
  activeStreams = [],
  onOpenLivePanel = null,
}) => {
  const [advancedFilter, setAdvancedFilter] = useState(null);
  const [activeQuickFilter, setActiveQuickFilter] = useState("");
  const [pageSize, setPageSize] = useState(16);
  const [page, setPage] = useState(1);
  const [slotSelected, setSlotSelected] = useState(null);
  const [dragTileId, setDragTileId] = useState("");
  const dragTileIdRef = useRef("");
  const dragOverTileIdRef = useRef("");
  const [orderedTiles, setOrderedTiles] = useState([]);
  const [viewportWidth, setViewportWidth] = useState(typeof window !== "undefined" ? window.innerWidth : 1440);
  const lastAdvancedFilterRef = useRef("");

  const liveTiles = useMemo(() => {
    const byDevice = new Map(listContratos.map((c) => [String(c.FKLokDeviceID || ""), c]));
    const base = (activeStreams || []).map((stream, idx) => {
      const contrato = byDevice.get(String(stream?.deviceId || "")) || {};
      return {
        id: String(stream?.streamId || `${stream?.deviceId || "device"}-${idx}`),
        deviceId: String(stream?.deviceId || contrato.FKLokDeviceID || ""),
        streamId: String(stream?.streamId || stream?.streamUrl || stream?.url || ""),
        streamUrl: resolveLiveStreamUrl(stream),
        contrato,
        FKLokDeviceID: contrato.FKLokDeviceID || stream?.deviceId || "N/D",
        NombreEmpresa: contrato.NombreEmpresa || "N/D",
        DescripcionRuta: contrato.DescripcionRuta || "N/D",
        TipoReporte: contrato.TipoReporte || "En vivo",
        PlacaTruck: contrato.PlacaTruck || "N/D",
      };
    });

    return base;
  }, [activeStreams, listContratos]);

  useEffect(() => {
    setOrderedTiles((prev) => {
      if (!Array.isArray(prev) || prev.length === 0) return liveTiles;

      const nextById = new Map(liveTiles.map((tile) => [tile.id, tile]));
      const kept = prev
        .map((tile) => nextById.get(tile.id))
        .filter(Boolean);

      const keptIds = new Set(kept.map((tile) => tile.id));
      const appended = liveTiles.filter((tile) => !keptIds.has(tile.id));

      return [...kept, ...appended];
    });
  }, [liveTiles]);

  const isStreamingOn = (contrato) => {
    const value = contrato?.StreamingOn ?? contrato?.streamingOn;
    return value === true || value === "true" || value === 1 || value === "1";
  };

  const isTimeExceeded = (contrato) => {
    const currentTime = new Date();
    const reportTime = new Date(contrato?.LoksysServerTime);
    if (Number.isNaN(reportTime.getTime())) return false;
    return currentTime - reportTime >= 30 * 60 * 1000;
  };

  const quickMatch = (contrato, key) => {
    if (!contrato) return false;
    if (key === "moving") return String(contrato?.IconMoving || "").includes("moving");
    if (key === "stop") return String(contrato?.IconMoving || "").includes("stop");
    if (key === "stopAlert") return String(contrato?.IconMoving || "").includes("stopAlert");
    if (key === "desvioalerta") return String(contrato?.IconDesvio || "").includes("desvioalerta");
    if (key === "batt-low") return String(contrato?.icon_bat || "").includes("batt-low");
    if (key === "candado")
      return (
        String(contrato?.IconLocked || "").includes("candado") ||
        String(contrato?.IconLocked || "").includes("openedpadlockalerta")
      );
    if (key === "tiempo") return isTimeExceeded(contrato);
    if (key === "streaming") return isStreamingOn(contrato);
    if (key === "noGps") return contrato?.bitGPS === true;
    if (key === "noTrayecto")
      return !contrato?.DescripcionRuta || String(contrato?.DescripcionRuta || "").trim() === "";
    return false;
  };

  const filteredByQuick = useMemo(() => {
    if (!activeQuickFilter) return orderedTiles;
    const byDevice = new Map(listContratos.map((c) => [String(c?.FKLokDeviceID || ""), c]));
    return orderedTiles.filter((tile) =>
      quickMatch(byDevice.get(String(tile?.FKLokDeviceID || "")), activeQuickFilter)
    );
  }, [orderedTiles, activeQuickFilter, listContratos]);

  const filteredTiles = useMemo(
    () => applyTrafficFilters(filteredByQuick, advancedFilter),
    [filteredByQuick, advancedFilter]
  );

  const totalPages = Math.max(1, Math.ceil(filteredTiles.length / pageSize));
  const effectivePage = Math.min(page, totalPages);
  const start = (effectivePage - 1) * pageSize;
  const visibleTiles = filteredTiles.slice(start, start + pageSize);
  const visibleTileIds = useMemo(
    () => new Set(visibleTiles.map((tile) => tile.id)),
    [visibleTiles]
  );
  const listCandidates = useMemo(
    () => filteredTiles.filter((tile) => !visibleTileIds.has(tile.id)),
    [filteredTiles, visibleTileIds]
  );
  const desktopCols = Math.sqrt(pageSize);
  const gridCols =
    viewportWidth < 768
      ? 1
      : viewportWidth < 1200
        ? Math.min(2, desktopCols)
        : desktopCols;
  const gridGap = pageSize === 16 ? 8 : 10;
  const listHeight = viewportWidth < 768 ? "280px" : viewportWidth < 1200 ? "420px" : "600px";
  const controlBtnHeight = 36;

  useEffect(() => {
    const onResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => {
    if (document.getElementById("lives-grid-empty-state-styles")) return;
    const style = document.createElement("style");
    style.id = "lives-grid-empty-state-styles";
    style.innerHTML = `
      @keyframes livesGridEmptyPulse {
        0% {
          transform: scale(1);
          box-shadow: 0 16px 40px rgba(0,102,153,0.18), 0 0 0 0 rgba(0,102,153,0.28);
        }
        50% {
          transform: scale(1.06);
          box-shadow: 0 20px 46px rgba(0,102,153,0.24), 0 0 0 16px rgba(0,102,153,0);
        }
        100% {
          transform: scale(1);
          box-shadow: 0 16px 40px rgba(0,102,153,0.18), 0 0 0 0 rgba(0,102,153,0);
        }
      }
    `;
    document.head.appendChild(style);
  }, []);

  const quickCounters = useMemo(() => {
    const safe = Array.isArray(listContratos) ? listContratos : [];
    return {
      noTrayecto: safe.filter((c) => quickMatch(c, "noTrayecto")).length,
      moving: safe.filter((c) => quickMatch(c, "moving")).length,
      stop: safe.filter((c) => quickMatch(c, "stop")).length,
      stopAlert: safe.filter((c) => quickMatch(c, "stopAlert")).length,
      desvioalerta: safe.filter((c) => quickMatch(c, "desvioalerta")).length,
      "batt-low": safe.filter((c) => quickMatch(c, "batt-low")).length,
      candado: safe.filter((c) => quickMatch(c, "candado")).length,
      tiempo: safe.filter((c) => quickMatch(c, "tiempo")).length,
      streaming: safe.filter((c) => quickMatch(c, "streaming")).length,
      noGps: safe.filter((c) => quickMatch(c, "noGps")).length,
    };
  }, [listContratos]);

  const replaceTile = (tile) => {
    if (slotSelected === null) return;
    const targetTileId = visibleTiles[slotSelected]?.id;
    const targetGlobal = orderedTiles.findIndex((x) => x.id === targetTileId);
    const sourceGlobal = orderedTiles.findIndex((x) => x.id === tile.id);
    if (targetGlobal < 0 || sourceGlobal < 0 || targetGlobal === sourceGlobal) return;
    setOrderedTiles((prev) => swapByIndex(prev, sourceGlobal, targetGlobal));
    setSlotSelected(null);
  };

  const extractTargetTileId = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    const tileNode = target?.closest?.("[data-tile-id]");
    return tileNode?.getAttribute("data-tile-id") || "";
  };

  const swapByTileIds = (sourceTileId, targetTileId) => {
    if (!sourceTileId || !targetTileId || sourceTileId === targetTileId) return;
    const from = orderedTiles.findIndex((x) => x.id === sourceTileId);
    const to = orderedTiles.findIndex((x) => x.id === targetTileId);
    if (from < 0 || to < 0 || from === to) return;
    setOrderedTiles((prev) => swapByIndex(prev, from, to));
  };

  const handleTileDragStart = (event, tileId) => {
    setDragTileId(tileId);
    dragTileIdRef.current = tileId;
    dragOverTileIdRef.current = "";
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.dropEffect = "move";
    event.dataTransfer.setData("application/x-live-tile-id", tileId);
    event.dataTransfer.setData("text/plain", tileId);
  };

  const handleTileDragEnter = (tileId) => {
    dragOverTileIdRef.current = tileId;
  };

  const handleTileDragEnd = () => {
    const sourceTileId = dragTileIdRef.current || dragTileId;
    const targetTileId = dragOverTileIdRef.current;
    swapByTileIds(sourceTileId, targetTileId);
    setDragTileId("");
    dragTileIdRef.current = "";
    dragOverTileIdRef.current = "";
  };

  const onDrop = (event, toLocal) => {
    event.preventDefault();
    const targetTileId = extractTargetTileId(event) || visibleTiles[toLocal]?.id || "";
    const droppedId =
      event.dataTransfer?.getData("application/x-live-tile-id") ||
      event.dataTransfer?.getData("text/plain") ||
      "";
    const sourceTileId = droppedId || dragTileIdRef.current || dragTileId;
    swapByTileIds(sourceTileId, targetTileId);
    handleTileDragEnd();
  };

  const goPrevPage = () => {
    setPage((prev) => Math.max(1, prev - 1));
  };

  const goNextPage = () => {
    setPage((prev) => Math.min(totalPages, prev + 1));
  };

  const handlePrevClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    goPrevPage();
  };

  const handleNextClick = (event) => {
    event.preventDefault();
    event.stopPropagation();
    goNextPage();
  };

  const handleTileClick = (idx, tile) => {
    setSlotSelected(idx);
    if (!onOpenLivePanel) return;
    const contrato = tile?.contrato?.FKLokDeviceID
      ? tile.contrato
      : {
          FKLokDeviceID: tile?.deviceId || tile?.FKLokDeviceID || "",
          NombreEmpresa: tile?.NombreEmpresa || "N/D",
          DescripcionRuta: tile?.DescripcionRuta || "N/D",
          TipoReporte: tile?.TipoReporte || "En vivo",
          PlacaTruck: tile?.PlacaTruck || "N/D",
        };
    onOpenLivePanel(contrato, tile?.streamUrl || "");
  };

  const controlButtonStyle = {
    minHeight: 40,
    height: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 8,
    fontSize: 14,
    lineHeight: 1,
  };

  return (
    <div className="card cardContainerTrafico" style={{ marginTop: 12, height: "80vh" }}>
      <div
        className="card-body"
        style={{
          height: "100%",
          display: "grid",
          gridTemplateRows: "auto auto auto 1fr",
          gap: 12,
          overflowY: "auto",
        }}
      >
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
          <div className="d-flex align-items-center gap-2">
            <button
              className="btn btn-outline-secondary btn-sm"
              data-bs-toggle="offcanvas"
              data-bs-target="#offcanvasFilterBody"
              style={{
                ...controlButtonStyle,
                gap: 8,
                padding: "0 14px",
                fontWeight: 500,
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  width: 14,
                  height: 14,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  color: "#5f7892",
                  flex: "0 0 auto",
                }}
              >
                <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M3 5h18" />
                  <path d="M6 12h12" />
                  <path d="M10 19h4" />
                </svg>
              </span>
              <span>Filtros avanzados</span>
            </button>
            <div className="btn-group" role="group" aria-label="Tamano de grilla">
              {PAGE_SIZES.map((size) => (
                <button
                  key={size}
                  className={`btn btn-sm ${pageSize === size ? "btn-primary" : "btn-outline-primary"}`}
                  style={{
                    ...controlButtonStyle,
                    minWidth: 46,
                    padding: "0 12px",
                    fontWeight: 500,
                  }}
                  onClick={() => { setPageSize(size); setPage(1); }}
                >
                  {size}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="countersColorsTrafico mb-2">
          <div className={`counterItem ${activeQuickFilter === "noTrayecto" ? "active" : "inactive"}`} onClick={() => setActiveQuickFilter((p) => (p === "noTrayecto" ? "" : "noTrayecto"))}>
            <div className="notification">
              <img src="/images/trayectonull.png" alt="Sin ruta" width="25" height="25" />
              <div className="notificationCount">{quickCounters.noTrayecto}</div>
            </div>
          </div>
          <div className={`counterItem ${activeQuickFilter === "moving" ? "active" : "inactive"}`} onClick={() => setActiveQuickFilter((p) => (p === "moving" ? "" : "moving"))}>
            <div className="notification"><img src="/images/moving.png" alt="Moving" /><div className="notificationCount">{quickCounters.moving}</div></div>
          </div>
          <div className={`counterItem ${activeQuickFilter === "stop" ? "active" : "inactive"}`} onClick={() => setActiveQuickFilter((p) => (p === "stop" ? "" : "stop"))}>
            <div className="notification"><img src="/images/stop.png" alt="Stop" /><div className="notificationCount">{quickCounters.stop}</div></div>
          </div>
          <div className={`counterItem ${activeQuickFilter === "stopAlert" ? "active" : "inactive"}`} onClick={() => setActiveQuickFilter((p) => (p === "stopAlert" ? "" : "stopAlert"))}>
            <div className="notification"><img src="/images/stopAlert.png" alt="Stop alerta" /><div className="notificationCount">{quickCounters.stopAlert}</div></div>
          </div>
          <div className={`counterItem ${activeQuickFilter === "desvioalerta" ? "active" : "inactive"}`} onClick={() => setActiveQuickFilter((p) => (p === "desvioalerta" ? "" : "desvioalerta"))}>
            <div className="notification"><img src="/images/desvioalerta.png" alt="Desvio" /><div className="notificationCount">{quickCounters.desvioalerta}</div></div>
          </div>
          <div className={`counterItem ${activeQuickFilter === "batt-low" ? "active" : "inactive"}`} onClick={() => setActiveQuickFilter((p) => (p === "batt-low" ? "" : "batt-low"))}>
            <div className="notification"><img src="/images/batt-low.png" alt="Bateria baja" width="25" height="25" /><div className="notificationCount">{quickCounters["batt-low"]}</div></div>
          </div>
          <div className={`counterItem ${activeQuickFilter === "candado" ? "active" : "inactive"}`} onClick={() => setActiveQuickFilter((p) => (p === "candado" ? "" : "candado"))}>
            <div className="notification"><img src="/images/openedpadlockalerta.gif" alt="Candado" width="25" height="25" /><div className="notificationCount">{quickCounters.candado}</div></div>
          </div>
          <div className={`counterItem ${activeQuickFilter === "tiempo" ? "active" : "inactive"}`} onClick={() => setActiveQuickFilter((p) => (p === "tiempo" ? "" : "tiempo"))}>
            <div className="notification"><img src="/images/timer-regular-24.png" alt="Tiempo" width="25" height="25" /><div className="notificationCount">{quickCounters.tiempo}</div></div>
          </div>
          <div className={`counterItem ${activeQuickFilter === "streaming" ? "active" : "inactive"}`} onClick={() => setActiveQuickFilter((p) => (p === "streaming" ? "" : "streaming"))}>
            <div className="notification"><span className="en-vivo-live-dot" /><div className="notificationCount">{quickCounters.streaming}</div></div>
          </div>
          <div className={`counterItem ${activeQuickFilter === "noGps" ? "active" : "inactive"}`} onClick={() => setActiveQuickFilter((p) => (p === "noGps" ? "" : "noGps"))}>
            <div className="notification"><img src="/images/NoGPS.png" alt="NoGPS" width="25" height="25" /><div className="notificationCount">{quickCounters.noGps}</div></div>
          </div>
        </div>

        <div className="d-flex justify-content-center align-items-center gap-2 pt-1 pb-1 border-top border-bottom" style={{ background: "#fff", zIndex: 2 }}>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={effectivePage <= 1}
            onClick={handlePrevClick}
            onMouseDown={(event) => event.stopPropagation()}
          >
            Anterior
          </button>
          <span>Pagina {effectivePage} de {totalPages}</span>
          <button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={effectivePage >= totalPages}
            onClick={handleNextClick}
            onMouseDown={(event) => event.stopPropagation()}
          >
            Siguiente
          </button>
        </div>

        {filteredTiles.length === 0 ? (
          <div
            style={{
              minHeight: 0,
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              padding: 24,
            }}
          >
            <div
              style={{
                width: "100%",
                height: "100%",
                minHeight: 320,
                borderRadius: 18,
                border: "1px dashed #c7d7e6",
                background: "linear-gradient(180deg, #f8fbfe 0%, #eef5fb 100%)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                padding: 32,
              }}
            >
              <div style={{ maxWidth: 520 }}>
                <div
                  aria-hidden="true"
                  style={{
                    width: 84,
                    height: 84,
                    margin: "0 auto 20px",
                    borderRadius: "50%",
                    background: "radial-gradient(circle at 30% 30%, #2ea4b1 0%, #006699 70%)",
                    color: "#fff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 34,
                    fontWeight: 700,
                    boxShadow: "0 16px 40px rgba(0,102,153,0.18)",
                    animation: "livesGridEmptyPulse 1.9s ease-in-out infinite",
                    willChange: "transform, box-shadow",
                  }}
                >
                  Live
                </div>
                <div
                  style={{
                    fontSize: viewportWidth < 768 ? 24 : 32,
                    lineHeight: 1.2,
                    fontWeight: 700,
                    color: "#1d3d5c",
                  }}
                >
                  Acá se muestran todos los lives
                </div>
                <div
                  style={{
                    marginTop: 12,
                    fontSize: viewportWidth < 768 ? 14 : 16,
                    color: "#5f7892",
                  }}
                >
                  Cuando haya transmisiones activas apareceran aquí automáticamente.
                </div>
              </div>
            </div>
          </div>
        ) : (
        <div className="row g-2" style={{ minHeight: 0 }}>
          <div className="col-12 col-xl-9 d-flex" style={{ minHeight: 0 }}>
            <div className="card w-100" style={{ overflow: "visible" }}>
              <div className="card-body p-2" style={{ overflow: "visible" }}>
                <div
                  style={{
                    display: "grid",
                    gridTemplateColumns: `repeat(${gridCols}, minmax(0, 1fr))`,
                    gap: gridGap,
                    width: "100%",
                    overflowY: "visible",
                    overflowX: "hidden",
                    paddingRight: 4,
                    paddingBottom: 8,
                    alignContent: "start",
                  }}
                  onDragOver={(e) => e.preventDefault()}
                >
              {visibleTiles.map((tile, idx) => (
                <div
                  key={tile.id}
                  data-tile-id={tile.id}
                  className="card"
                  style={{
                    border: slotSelected === idx ? "2px solid #006699" : "1px solid #dfe5ec",
                    boxShadow: slotSelected === idx ? "0 0 0 2px rgba(0,102,153,0.15)" : "none",
                    cursor: "grab",
                    overflow: "hidden",
                    display: "grid",
                    gridTemplateRows: "auto",
                    minHeight: 0,
                  }}
                  draggable
                  onDragStart={(e) => handleTileDragStart(e, tile.id)}
                  onDragEnter={() => handleTileDragEnter(tile.id)}
                  onDragEnd={handleTileDragEnd}
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                  }}
                  onDrop={(e) => onDrop(e, idx)}
                  onClick={() => handleTileClick(idx, tile)}
                  onDoubleClick={(e) => {
                    const el = e.currentTarget.querySelector("iframe");
                    if (el?.requestFullscreen) el.requestFullscreen().catch(() => {});
                  }}
                >
                  <div
                    style={{ width: "100%", aspectRatio: "16 / 9", minHeight: 0, overflow: "hidden", borderRadius: 8, position: "relative" }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = "move";
                    }}
                    onDrop={(e) => onDrop(e, idx)}
                  >
                    <iframe
                      title={tile.id}
                      src={tile.streamUrl}
                      allow="autoplay; fullscreen"
                      scrolling="no"
                      style={{ width: "100%", height: "100%", border: 0, display: "block", pointerEvents: "none" }}
                    />
                    <div
                      style={{
                        position: "absolute",
                        inset: 0,
                        zIndex: 3,
                        cursor: slotSelected === idx ? "grabbing" : "grab",
                        pointerEvents: "none",
                      }}
                      title="Click para seleccionar. Arrastra para reordenar. Doble click pantalla completa."
                    />
                    <div
                      style={{
                        position: "absolute",
                        left: 8,
                        right: 8,
                        bottom: 8,
                        padding: "8px 10px",
                        borderRadius: 8,
                        background: "linear-gradient(180deg, rgba(0,0,0,0.1) 0%, rgba(0,0,0,0.72) 100%)",
                        color: "#fff",
                        zIndex: 2,
                        pointerEvents: "none",
                      }}
                    >
                      <div style={{ fontWeight: 700, lineHeight: 1.1 }}>{tile.FKLokDeviceID}</div>
                      <div style={{ fontSize: 12, opacity: 0.9 }}>{tile.NombreEmpresa} · {tile.PlacaTruck}</div>
                    </div>
                    {slotSelected === idx && (
                      <div
                        style={{
                          position: "absolute",
                          top: 8,
                          right: 8,
                          zIndex: 4,
                          background: "#006699",
                          color: "#fff",
                          borderRadius: 999,
                          fontSize: 11,
                          padding: "4px 8px",
                          lineHeight: 1,
                          pointerEvents: "none",
                        }}
                      >
                        Seleccionada
                      </div>
                    )}
                  </div>
                </div>
              ))}
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 col-xl-3 d-flex" style={{ minHeight: 0 }}>
            <div className="card w-100" style={{ height: "100%", overflow: "hidden" }}>
              <div className="card-body d-flex flex-column" style={{ minHeight: 0 }}>
                <h6>Lista de camaras</h6>
                <small className="text-muted d-block mb-2">
                  Selecciona una card y reemplaza desde esta lista o arrastra una camara al grid.
                </small>
                <div style={{ height: listHeight, minHeight: 0, overflowY: "auto", overflowX: "hidden", paddingRight: 4 }}>
                  {listCandidates.map((tile) => (
                    <div
                      key={tile.id}
                      className="btn btn-light w-100 text-start mb-2"
                      role="button"
                      tabIndex={0}
                      draggable
                      onDragStart={(e) => handleTileDragStart(e, tile.id)}
                      onDragEnd={handleTileDragEnd}
                      onClick={() => replaceTile(tile)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          replaceTile(tile);
                        }
                      }}
                    >
                      <div className="fw-semibold">{tile.FKLokDeviceID}</div>
                      <small className="text-muted">{tile.NombreEmpresa}</small>
                    </div>
                  ))}
                  {listCandidates.length === 0 && (
                    <small className="text-muted">
                      No hay camaras disponibles fuera de la grilla actual.
                    </small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
        )}
      </div>

      <div className="offcanvas offcanvas-end" data-bs-scroll="true" data-bs-backdrop="false" tabIndex="-1" id="offcanvasFilterBody" aria-labelledby="offcanvasFilterBodyLabel" style={{ width: "36rem", zIndex: 2105 }}>
        <div className="offcanvas-header">
          <h5 className="offcanvas-title" id="offcanvasFilterBodyLabel">Filtros de trafico</h5>
          <button type="button" className="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
        </div>
        <div className="offcanvas-body">
          <FilterBodyOffcanvas
            contratos={listContratos}
            listContratos={listContratos}
            onFiltersChange={(f) => {
              const nextKey = JSON.stringify(f || {});
              const changed = nextKey !== lastAdvancedFilterRef.current;
              lastAdvancedFilterRef.current = nextKey;
              setAdvancedFilter(f);
              if (changed) setPage(1);
            }}
            savedFilter={() => {}}
            newValueFilter={() => {}}
            deleteNewFilter={() => {}}
          />
        </div>
      </div>
    </div>
  );
};

export default LivesGridManager;









